"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Gauge, ShieldCheck, Coins, Droplets, Timer, Sparkles, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
export default function TechnicalBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const coreBenefits = [{
    id: "perf",
    title: "Desempenho de alto impacto",
    tag: "Performance",
    description: "Impacto uniforme e atomização milimétrica para ciclos de higienização rápidos e consistentes.",
    detail1: "Distribuição homogénea de caudal",
    detail2: "Pressão calibrada sem perdas de carga"
  }, {
    id: "dur",
    title: "Durabilidade em ciclo contínuo",
    tag: "Fiabilidade",
    description: "Ligas técnicas e cerâmicas resistentes a corrosão, desgaste abrasivo e fluidos agressivos.",
    detail1: "Materiais certificados de grau industrial",
    detail2: "Menor frequência de paragens de manutenção"
  }, {
    id: "cost",
    title: "Eficiência de custos e recursos",
    tag: "Poupança",
    description: "Redução comprovada no consumo de água, energia e dosagem de agentes químicos de lavagem.",
    detail1: "Poupança expressiva de água e energia",
    detail2: "Retorno de investimento rápido e mensurável"
  }];
  const quickStats = [{
    id: "stat-water",
    value: "-35%",
    label: "Consumo de água",
    sublabel: "Face a métodos manuais ou bicos padrão"
  }, {
    id: "stat-time",
    value: "50%",
    label: "Redução no tempo de ciclo",
    sublabel: "Lavagens completas de cubas e reatores"
  }, {
    id: "stat-chem",
    value: "-25%",
    label: "Dosagem química",
    sublabel: "Aplicação homogénea sem desperdício"
  }, {
    id: "stat-rep",
    value: "100%",
    label: "Repetibilidade técnica",
    sublabel: "Conformidade sanitária e CIP garantida"
  }];
  return <section id="technicalbenefits" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Tecnologia de Lavagem Industrial
          </Badge>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-6">
            Vantagens técnicas em limpeza e lavagem industrial
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Sistemas calibrados para máxima precisão, concebidos pela Eurocontrol e Spraying Systems Co. para proteger a sua operação e reduzir custos operacionais.
          </p>
        </div>

        {/* 3 Main Highlight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16 lg:mb-20">
          {coreBenefits.map((benefit, i) => <motion.div key={benefit.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: i * 0.12,
          ease: "easeOut"
        }} className="h-full flex flex-col">
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between">
                <div>
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="size-12 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                        {i === 0 && <Gauge className="size-6 text-primary" />}
                        {i === 1 && <ShieldCheck className="size-6 text-primary" />}
                        {i === 2 && <Coins className="size-6 text-primary" />}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {benefit.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground pt-2">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </CardDescription>

                    <div className="pt-2 space-y-2 border-t border-border/60">
                      <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{benefit.detail1}</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{benefit.detail2}</span>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Technical Metric Indicators Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="rounded-2xl bg-muted p-8 lg:p-10 border border-border mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {quickStats.map((stat, i) => <div key={stat.id} data-index={i} className="flex flex-col text-center sm:text-left border-b sm:border-b-0 sm:border-r last:border-0 border-border pb-4 sm:pb-0 sm:pr-4 last:pr-0">
                <span className="text-3xl lg:text-4xl font-bold text-primary tracking-tight font-default">
                  {stat.value}
                </span>
                <span className="text-sm font-semibold text-foreground mt-1">
                  {stat.label}
                </span>
                <span className="text-xs text-muted-foreground mt-0.5">
                  {stat.sublabel}
                </span>
              </div>)}
          </div>
        </motion.div>

        {/* Bottom Detailed Matrix / Key Advantages Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-card rounded-2xl p-6 sm:p-10 border border-border">
          <div className="space-y-4">
            <Badge variant="outline" className="w-fit text-xs font-medium">
              Engenharia de Aplicação
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Tecnologia precisa para os setores mais exigentes
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Desde cabeçotes rotativos TankJet até bicos de alta pressão e sistemas automatizados, configuramos a solução exata para a geometria dos seus equipamentos e normas sanitárias.
            </p>
            <div className="pt-2 flex flex-wrap gap-3">
              <Button asChild size="default">
                <Link to="/TechnicalSolutionsPage">
                  Explorar soluções técnicas
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="default">
                <Link to="/ContactsPage">
                  Falar com um engenheiro
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-background border border-border/80">
              <Droplets className="size-5 text-primary mb-2" />
              <h4 className="text-sm font-semibold text-foreground mb-1">Impacto constante</h4>
              <p className="text-xs text-muted-foreground">Cobertura de 360 graus sem pontos cegos em reservatórios e tubagens.</p>
            </div>
            
            <div className="p-4 rounded-xl bg-background border border-border/80">
              <Timer className="size-5 text-primary mb-2" />
              <h4 className="text-sm font-semibold text-foreground mb-1">Automação CIP</h4>
              <p className="text-xs text-muted-foreground">Integração simplificada com ciclos de limpeza Clean-In-Place automatizados.</p>
            </div>

            <div className="p-4 rounded-xl bg-background border border-border/80">
              <ShieldCheck className="size-5 text-primary mb-2" />
              <h4 className="text-sm font-semibold text-foreground mb-1">Conformidade total</h4>
              <p className="text-xs text-muted-foreground">Compatível com normas FDA, 3-A Sanitary Standards e diretrizes CE.</p>
            </div>

            <div className="p-4 rounded-xl bg-background border border-border/80">
              <Sparkles className="size-5 text-primary mb-2" />
              <h4 className="text-sm font-semibold text-foreground mb-1">Suporte técnico local</h4>
              <p className="text-xs text-muted-foreground">Acompanhamento e testes de pulverização dedicados em Portugal desde 1993.</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}